import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Download, FileText, AlertCircle, CheckCircle, X, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FeedGame {
  id?: string;
  title: string;
  description?: string;
  thumbnail?: string;
  game_url?: string;
  category: string;
  featured?: boolean;
  trending?: boolean;
  is_new?: boolean;
  hidden?: boolean;
  originalCategory?: string;
  validationIssues?: string[];
}

const AdminGamesFeed = () => {
  const [feedUrl, setFeedUrl] = useState('');
  const [feedGames, setFeedGames] = useState<FeedGame[]>([]);
  const [filteredGames, setFilteredGames] = useState<FeedGame[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGames, setSelectedGames] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();

  // Fetch categories for validation
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('order_index');
      if (error) throw error;
      return data || [];
    },
  });

  const categoryMap = new Map(categories.map(cat => [cat.name.toLowerCase(), cat.slug]));

  // Enhanced category mapping - maps common feed categories to your categories
  const categoryMappings = {
    'arcade': 'arcade',
    'hypercasual': 'hypercasual',
    'girls': 'girls',
    'shooting': 'shooting',
    'boys': 'boys',
    'casual': 'hypercasual',
    'skill': 'puzzle',
    'car': 'racing',
    'driving': 'racing',
    'sport': 'sports',
    'football': 'soccer',
    'basketball': 'sports',
    'run': 'action',
    'jump': 'action',
    'fight': 'action',
    'fighting': 'action',
    'cooking': 'cooking',
    'kitchen': 'cooking',
    'restaurant': 'cooking',
    'stickman': 'stickman',
    'stick': 'stickman',
    'io': 'io',
    '.io': 'io',
    'multiplayer': 'multiplayer',
    'multi': 'multiplayer',
    '2player': '2-player',
    '2-player': '2-player',
    'two-player': '2-player',
    'twoplayer': '2-player',
    '3d': '3d',
    'three-d': '3d',
    'adventure': 'adventure',
    'quest': 'adventure',
    'explore': 'adventure',
    'baby': 'baby-hazel',
    'hazel': 'baby-hazel',
    'baby-hazel': 'baby-hazel',
    'bejeweled': 'bejeweled',
    'jewel': 'bejeweled',
    'gems': 'bejeweled',
    'match3': 'bejeweled',
    'match-3': 'bejeweled',
    'clicker': 'clicker',
    'click': 'clicker',
    'idle': 'clicker',
    'incremental': 'clicker',
    'memory': 'puzzle',
    'block': 'puzzle',
    'escape': 'adventure',
    'simulation': 'strategy',
    'bowling': 'sports',
    'coloring': 'girls',
    'ball': 'sports',
    'drawing': 'girls'
  };

  const mapCategory = (originalCategory: string): { category: string; mapped: boolean } => {
    const lowerCategory = originalCategory.toLowerCase();
    
    // First check if category exists directly
    if (categoryMap.has(lowerCategory)) {
      return { category: categoryMap.get(lowerCategory)!, mapped: false };
    }
    
    // Then check mappings
    if (categoryMappings[lowerCategory]) {
      return { category: categoryMappings[lowerCategory], mapped: true };
    }
    
    // Default to puzzle for unmapped categories
    return { category: 'puzzle', mapped: true };
  };

  const loadFeedMutation = useMutation({
    mutationFn: async (url: string) => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch feed: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      console.log('Feed data received:', data);
      
      let games: any[] = [];
      
      // Handle different feed formats
      if (Array.isArray(data)) {
        games = data;
      } else if (data.items && Array.isArray(data.items)) {
        // JSON Feed format (like GamePix)
        games = data.items;
      } else if (data.games && Array.isArray(data.games)) {
        games = data.games;
      } else if (data.data && Array.isArray(data.data)) {
        games = data.data;
      } else {
        throw new Error('Invalid feed format - no games array found');
      }

      console.log(`Processing ${games.length} games from feed`);

      // Process all games and separate valid from invalid
      const allProcessedGames: FeedGame[] = [];
      const validGames: FeedGame[] = [];

      games.forEach((game: any) => {
        const validationIssues: string[] = [];
        
        // Map common field variations (including GamePix format)
        const title = game.title || game.name || '';
        const description = game.description || game.desc || game.summary || '';
        const thumbnail = game.banner_image || game.thumbnail || game.thumb || game.image || game.img || '';
        const game_url = game.url || game.game_url || game.link || game.play_url || '';
        const originalCategory = game.category || game.cat || game.genre || '';

        // Validate required fields
        if (!title) validationIssues.push('Missing title');
        if (!originalCategory) validationIssues.push('Missing category');
        if (!game_url) validationIssues.push('Missing game URL');

        // Map category
        const { category, mapped } = mapCategory(originalCategory);

        const processedGame: FeedGame = {
          title,
          description,
          thumbnail,
          game_url,
          category,
          originalCategory,
          featured: game.featured || false,
          trending: game.trending || false,
          is_new: game.is_new !== undefined ? game.is_new : true,
          hidden: game.hidden || false,
          validationIssues
        };

        allProcessedGames.push(processedGame);

        // Only add to valid games if no validation issues
        if (validationIssues.length === 0) {
          validGames.push(processedGame);
        }
      });

      setFeedGames(allProcessedGames);
      setFilteredGames(validGames);
      setSelectedGames(new Set(validGames.map((_, index) => index)));
      
      const invalidCount = allProcessedGames.length - validGames.length;
      
      if (validGames.length === 0) {
        toast.error('No valid games found in the feed. Check the validation issues below.');
      } else {
        toast.success(`Found ${validGames.length} valid games${invalidCount > 0 ? ` (${invalidCount} filtered out)` : ''}`);
      }
    },
    onError: (error: any) => {
      console.error('Feed load error:', error);
      toast.error(`Failed to load feed: ${error.message}`);
      setFeedGames([]);
      setFilteredGames([]);
    },
  });

  const importGamesMutation = useMutation({
    mutationFn: async (games: FeedGame[]) => {
      const { error } = await supabase
        .from('games')
        .insert(games.map(game => ({
          title: game.title,
          description: game.description,
          thumbnail: game.thumbnail,
          game_url: game.game_url,
          category: game.category,
          featured: game.featured,
          trending: game.trending,
          is_new: game.is_new,
          hidden: game.hidden
        })));
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      queryClient.invalidateQueries({ queryKey: ['games'] });
      toast.success(`Successfully imported ${selectedGames.size} games`);
      setFeedGames([]);
      setFilteredGames([]);
      setSelectedGames(new Set());
      setFeedUrl('');
    },
    onError: (error: any) => {
      console.error('Import error:', error);
      toast.error(`Failed to import games: ${error.message}`);
    },
  });

  const handleLoadFeed = () => {
    if (!feedUrl.trim()) {
      toast.error('Please enter a feed URL');
      return;
    }
    setIsLoading(true);
    loadFeedMutation.mutate(feedUrl.trim(), {
      onSettled: () => setIsLoading(false),
    });
  };

  const handleImportSelected = () => {
    const gamesToImport = Array.from(selectedGames).map(index => filteredGames[index]);
    if (gamesToImport.length === 0) {
      toast.error('Please select games to import');
      return;
    }
    importGamesMutation.mutate(gamesToImport);
  };

  const toggleGameSelection = (index: number) => {
    const newSelection = new Set(selectedGames);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedGames(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedGames.size === filteredGames.length) {
      setSelectedGames(new Set());
    } else {
      setSelectedGames(new Set(filteredGames.map((_, index) => index)));
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-sm">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Import Games from Feed</h1>
        <p className="text-gray-600 mt-2">Import games from external feeds or JSON files</p>
      </div>

      {/* Feed URL Input */}
      <Card className="p-6 bg-white/90 backdrop-blur-sm border border-gray-200/50 shadow-sm">
        <div className="space-y-4">
          <div>
            <Label htmlFor="feedUrl" className="text-gray-900 font-medium">Feed URL</Label>
            <div className="flex gap-3 mt-2">
              <Input
                id="feedUrl"
                value={feedUrl}
                onChange={(e) => setFeedUrl(e.target.value)}
                placeholder="https://example.com/games-feed.json"
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
              />
              <Button
                onClick={handleLoadFeed}
                disabled={isLoading || loadFeedMutation.isPending}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-6 shadow-lg"
              >
                <Download className="w-4 h-4 mr-2" />
                {isLoading ? 'Loading...' : 'Load from URL'}
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Enter the URL of a JSON feed containing game data
            </p>
          </div>
        </div>
      </Card>

      {/* Stats Card */}
      {feedGames.length > 0 && (
        <Card className="p-4 bg-white/90 backdrop-blur-sm border border-gray-200/50 shadow-sm">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" />
              <span className="text-gray-900 font-medium">Total: {feedGames.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-900 font-medium">Valid: {filteredGames.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-gray-900 font-medium">Filtered: {feedGames.length - filteredGames.length}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Valid Games Preview */}
      {filteredGames.length > 0 && (
        <Card className="bg-white/90 backdrop-blur-sm border border-gray-200/50 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Valid Games ({filteredGames.length})
                </h3>
                <p className="text-gray-600">
                  {selectedGames.size} of {filteredGames.length} games selected
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={toggleSelectAll}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                >
                  {selectedGames.size === filteredGames.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  onClick={handleImportSelected}
                  disabled={selectedGames.size === 0 || importGamesMutation.isPending}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium shadow-lg"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Selected
                </Button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200">
                  <TableHead className="w-12 text-gray-700 font-semibold"></TableHead>
                  <TableHead className="text-gray-700 font-semibold">Game</TableHead>
                  <TableHead className="text-gray-700 font-semibold">Category</TableHead>
                  <TableHead className="text-gray-700 font-semibold">Status</TableHead>
                  <TableHead className="text-gray-700 font-semibold">URL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGames.map((game, index) => (
                  <TableRow key={index} className="border-gray-200 hover:bg-gray-50/50">
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedGames.has(index)}
                        onChange={() => toggleGameSelection(index)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                          {game.thumbnail ? (
                            <img
                              src={game.thumbnail}
                              alt={game.title}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <FileText className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{game.title}</div>
                          {game.description && (
                            <div className="text-sm text-gray-600 max-w-xs truncate">
                              {game.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">{game.category}</Badge>
                        {game.originalCategory && game.originalCategory.toLowerCase() !== game.category && (
                          <div className="text-xs text-gray-500">
                            Mapped from: {game.originalCategory}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {game.featured && <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Featured</Badge>}
                        {game.trending && <Badge className="bg-orange-100 text-orange-800 border-orange-200">Trending</Badge>}
                        {game.is_new && <Badge className="bg-green-100 text-green-800 border-green-200">New</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {game.game_url ? (
                        <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Valid
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          No URL
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Filtered Games (Validation Issues) */}
      {feedGames.length > filteredGames.length && (
        <Card className="bg-white/90 backdrop-blur-sm border border-yellow-200 shadow-sm">
          <div className="p-6 border-b border-yellow-200">
            <h3 className="text-lg font-semibold text-yellow-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Filtered Games ({feedGames.length - filteredGames.length})
            </h3>
            <p className="text-yellow-700 mt-1">These games were excluded due to validation issues</p>
          </div>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-yellow-200">
                  <TableHead className="text-gray-700 font-semibold">Game</TableHead>
                  <TableHead className="text-gray-700 font-semibold">Original Category</TableHead>
                  <TableHead className="text-gray-700 font-semibold">Issues</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedGames.filter(game => game.validationIssues && game.validationIssues.length > 0).map((game, index) => (
                  <TableRow key={index} className="border-yellow-200 hover:bg-yellow-50/50">
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                          {game.thumbnail ? (
                            <img
                              src={game.thumbnail}
                              alt={game.title || 'Untitled'}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <FileText className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{game.title || 'Untitled'}</div>
                          {game.description && (
                            <div className="text-sm text-gray-600 max-w-xs truncate">
                              {game.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-gray-400 text-gray-600 bg-gray-50">
                        {game.originalCategory || 'None'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {game.validationIssues?.map((issue, i) => (
                          <Badge key={i} variant="outline" className="border-red-400 text-red-700 bg-red-50 mr-1">
                            {issue}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminGamesFeed;
